/**
 * SCROLL LOCK UTILITY
 *
 * Centralized scroll locking for drawers, modals, and popovers.
 * Handles iOS Safari properly with position: fixed approach.
 * Manages lock count to coordinate multiple overlays.
 *
 * @module ScrollLock
 */

(function() {
  'use strict';

  // =========================================================================
  // CONSTANTS
  // =========================================================================

  /**
   * Fallback timeout for transitionend events (in milliseconds).
   * Used when transitionend fails to fire (e.g., display:none, rapid state changes).
   * @constant {number}
   */
  var FALLBACK_TIMEOUT_MS = 400;

  // =========================================================================
  // STATE
  // =========================================================================

  // Keep track of how many times we've locked (for nested overlays)
  var lockCount = 0;

  // Store the scroll position before locking
  var scrollPosition = 0;

  // AbortController for managing transitionend listeners
  var transitionAbortController = null;

  // Track if this observer triggered a lock (to know if we should unlock)
  var observerLocked = false;

  // =========================================================================
  // CORE API
  // =========================================================================

  /**
   * Lock body scroll to prevent background scrolling.
   * @param {boolean} forceApply - Force apply styles even if already locked
   * @returns {number} Current lock count
   */
  function lockScroll(forceApply) {
    lockCount++;

    // Apply styles on first lock OR when forced (for overlay transitions)
    var shouldApply = lockCount === 1 || forceApply;

    if (shouldApply) {
      // Store current scroll position
      scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      // Add no-scroll class for desktop (overflow: hidden)
      document.documentElement.classList.add('no-scroll');

      // iOS-specific fix: position fixed with top offset
      // This prevents elastic scrolling and touch-based scroll on iOS
      document.body.style.position = 'fixed';
      document.body.style.top = '-' + scrollPosition + 'px';
      document.body.style.width = '100%';
    }

    return lockCount;
  }

  /**
   * Unlock body scroll, restoring normal scrolling behavior.
   * @returns {number} Current lock count
   */
  function unlockScroll() {
    lockCount--;

    // Only remove the lock styles when all locks are released
    if (lockCount <= 0) {
      lockCount = 0; // Prevent negative counts

      // Remove no-scroll class
      document.documentElement.classList.remove('no-scroll');

      // Restore body styles
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';

      // Restore scroll position
      window.scrollTo(0, scrollPosition);
    }

    return lockCount;
  }

  /**
   * Check if scroll is currently locked.
   * @returns {boolean}
   */
  function isLocked() {
    return lockCount > 0;
  }

  /**
   * Get current lock count.
   * @returns {number}
   */
  function getLockCount() {
    return lockCount;
  }

  /**
   * Force reset all locks (emergency use only).
   */
  function forceUnlock() {
    if (transitionAbortController) {
      transitionAbortController.abort();
      transitionAbortController = null;
    }
    lockCount = 0;
    unlockScroll();
  }

  /**
   * Force reset scroll lock state for overlay transitions.
   * Clears styles AND resets lockCount to 0.
   * Used when transitioning between FIXED overlays (e.g., Search → Cart).
   *
   * The new overlay will call lock() which starts fresh at lockCount = 1.
   * This prevents lockCount accumulation when rapidly switching overlays.
   */
  function forceReset() {
    // Abort any pending transition listeners
    if (transitionAbortController) {
      transitionAbortController.abort();
      transitionAbortController = null;
    }

    // Reset observer state to prevent interference
    observerLocked = false;

    // CRITICAL: Reset lockCount to 0 for clean overlay transition
    // Without this, rapid overlay switching causes lockCount to accumulate:
    //   1. Search opens: lockCount = 1
    //   2. Cart opens (conflict): forceReset clears styles, lock() increments to 2
    //   3. Cart closes: unlock() decrements to 1, scroll stays locked!
    lockCount = 0;

    // Remove styles synchronously
    document.documentElement.classList.remove('no-scroll');
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
  }

  // =========================================================================
  // GLOBAL API EXPOSURE
  // =========================================================================

  window.ScrollLock = {
    lock: lockScroll,
    unlock: unlockScroll,
    isLocked: isLocked,
    getLockCount: getLockCount,
    forceUnlock: forceUnlock,
    forceReset: forceReset
  };

  // =========================================================================
  // EARLY SEARCH OBSERVER
  // =========================================================================

  /**
   * Wait for overlay transition to complete, then execute callback.
   * @param {Element} overlay - The overlay element to watch
   * @param {Function} callback - Function to call when transition completes
   */
  function waitForOverlayTransition(overlay, callback) {
    // Abort any previous pending transition listener
    if (transitionAbortController) {
      transitionAbortController.abort();
    }

    // Create new AbortController for this transition
    transitionAbortController = new AbortController();
    var signal = transitionAbortController.signal;

    // Track if callback has been executed (prevents double-fire)
    var callbackExecuted = false;

    var executeCallback = function() {
      if (callbackExecuted || signal.aborted) {
        return;
      }
      callbackExecuted = true;
      transitionAbortController = null;
      callback();
    };

    // Listen for transitionend with abort signal
    overlay.addEventListener('transitionend', function(event) {
      if (event.propertyName === 'visibility' || event.propertyName === 'opacity') {
        executeCallback();
      }
    }, { signal: signal });

    // Fallback timeout in case transitionend doesn't fire
    var fallbackId = setTimeout(executeCallback, FALLBACK_TIMEOUT_MS);

    // If aborted, clear the fallback timeout
    signal.addEventListener('abort', function() {
      clearTimeout(fallbackId);
    });
  }

  /**
   * Initialize the MutationObserver for early search detection.
   * This provides scroll locking before theme.js fully initializes.
   */
  function initSearchObserver() {
    var searchElement = document.getElementById('Search');
    if (!searchElement) {
      return;
    }

    var observer = new MutationObserver(function(mutations) {
      mutations.forEach(function(mutation) {
        if (mutation.attributeName !== 'aria-hidden') return;

        var isOpen = searchElement.getAttribute('aria-hidden') === 'false';

        if (isOpen && !observerLocked) {
          lockScroll();
          observerLocked = true;
        } else if (!isOpen && observerLocked) {
          var overlay = document.querySelector('.PageOverlay');

          if (overlay) {
            waitForOverlayTransition(overlay, function() {
              if (observerLocked) {
                unlockScroll();
                observerLocked = false;
              }
            });
          } else {
            unlockScroll();
            observerLocked = false;
          }
        }
      });
    });

    observer.observe(searchElement, {
      attributes: true,
      attributeFilter: ['aria-hidden']
    });
  }

  // Initialize observer when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initSearchObserver);
  } else {
    initSearchObserver();
  }

})();
